// LT-330 — Scheduled callbacks section on the contact detail page.
//
// Shows the contact's scheduled-call commitments (pending + history).
// Operator actions: create new (manual), reschedule a pending row,
// cancel a pending row. Fired / cancelled / failed_to_fire rows are
// read-only.
//
// Per LT-330 light-LLD point 3: this is a section on the contact-
// detail page, not a separate tab — callbacks are part of the
// contact's state, not a navigational unit.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarClock, Plus, RotateCcw, X } from 'lucide-react';

import { agentsService } from '../services/agentsService';
import { campaignsAPI } from '../services/campaignsAPI';
import { contactsAPI } from '../services/contactsAPI';
import { sequencesAPI } from '../services/sequencesAPI';
import type { Agent } from '../types/agent';
import type { Campaign } from '../types/campaign';
import type {
  ScheduledCallback,
  ScheduledCallbackStatus,
} from '../types/contact';
import type { Sequence } from '../types/sequence';
import { Button, IconButton, Input, Pill, Select, type PillIntent } from '../ui';

const STATUS_INTENT: Record<ScheduledCallbackStatus, PillIntent> = {
  pending: 'warn',
  fired: 'ok',
  cancelled: 'neutral',
  failed_to_fire: 'danger',
};

const STATUS_LABEL: Record<ScheduledCallbackStatus, string> = {
  pending: 'Pending',
  fired: 'Fired',
  cancelled: 'Cancelled',
  failed_to_fire: 'Failed',
};

function fmt(when: string | null | undefined): string {
  if (!when) return '—';
  const d = new Date(when);
  return Number.isNaN(d.getTime()) ? when : d.toLocaleString();
}

function isoLocalForInput(when: string | Date): string {
  // Convert a Date / ISO string into the value shape <input type="datetime-local">
  // wants — local time with seconds stripped, no timezone.
  const d = typeof when === 'string' ? new Date(when) : when;
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function inputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

interface ScheduledCallbacksSectionProps {
  contactId: string;
}

export const ScheduledCallbacksSection: React.FC<ScheduledCallbacksSectionProps> = ({
  contactId,
}) => {
  const [items, setItems] = useState<ScheduledCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [createWhen, setCreateWhen] = useState('');
  const [createNotes, setCreateNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // LT-390: agent / sequence / campaign pickers in the create modal.
  // Backend `ScheduledCallbackCreate` already accepts the snapshot IDs;
  // the UI just wasn't surfacing them. Fetched lazily when the modal
  // opens so the section render path stays cheap.
  const [createAgentId, setCreateAgentId] = useState('');
  const [createSequenceId, setCreateSequenceId] = useState('');
  const [createCampaignId, setCreateCampaignId] = useState('');
  const [agentOptions, setAgentOptions] = useState<Agent[] | null>(null);
  const [sequenceOptions, setSequenceOptions] = useState<Sequence[] | null>(null);
  const [campaignOptions, setCampaignOptions] = useState<Campaign[] | null>(null);
  const [optionsLoading, setOptionsLoading] = useState(false);

  const [rescheduling, setRescheduling] = useState<string | null>(null);
  const [rescheduleWhen, setRescheduleWhen] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [cancelSaving, setCancelSaving] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rows = await contactsAPI.listScheduledCallbacks(contactId);
      setItems(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load scheduled callbacks');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // LT-390: fetch agent / sequence / campaign options when the create
  // modal opens. Cache for the lifetime of the section — picker lists
  // don't change often and refetching on every open is wasteful.
  useEffect(() => {
    if (!showCreate) return;
    if (agentOptions !== null && sequenceOptions !== null && campaignOptions !== null) return;
    setOptionsLoading(true);
    Promise.all([
      agentOptions === null
        ? agentsService.list({ includeSystem: true, includeDeprecated: false })
        : Promise.resolve(agentOptions),
      sequenceOptions === null
        ? sequencesAPI.list({ status: 'active' })
        : Promise.resolve(sequenceOptions),
      campaignOptions === null
        ? campaignsAPI.list({ status: ['active', 'draft'] })
        : Promise.resolve(campaignOptions),
    ])
      .then(([agents, sequences, campaigns]) => {
        setAgentOptions(agents);
        setSequenceOptions(sequences);
        setCampaignOptions(campaigns);
      })
      .catch((e) => {
        // Non-fatal — operator can still pick a time + notes. Surface so
        // they know agent/sequence/campaign association may be missing.
        setErr(
          e instanceof Error
            ? `Failed to load pickers: ${e.message}`
            : 'Failed to load agent / sequence / campaign options',
        );
      })
      .finally(() => setOptionsLoading(false));
  }, [showCreate, agentOptions, sequenceOptions, campaignOptions]);

  const pending = useMemo(() => items.filter((c) => c.status === 'pending'), [items]);
  const history = useMemo(() => items.filter((c) => c.status !== 'pending'), [items]);

  const handleCreate = async () => {
    const iso = inputToIso(createWhen);
    if (!iso) {
      setErr('Pick a valid date and time.');
      return;
    }
    setCreating(true);
    setErr(null);
    try {
      await contactsAPI.createScheduledCallback(contactId, {
        scheduled_at: iso,
        agent_id_snapshot: createAgentId || null,
        sequence_id_snapshot: createSequenceId || null,
        campaign_id_snapshot: createCampaignId || null,
        notes: createNotes.trim() || null,
      });
      setShowCreate(false);
      setCreateWhen('');
      setCreateNotes('');
      setCreateAgentId('');
      setCreateSequenceId('');
      setCreateCampaignId('');
      await fetchList();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduling) return;
    const iso = inputToIso(rescheduleWhen);
    if (!iso) {
      setErr('Pick a valid date and time.');
      return;
    }
    setRescheduleSaving(true);
    setErr(null);
    try {
      await contactsAPI.rescheduleScheduledCallback(contactId, rescheduling, iso);
      setRescheduling(null);
      setRescheduleWhen('');
      await fetchList();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Reschedule failed');
    } finally {
      setRescheduleSaving(false);
    }
  };

  const handleCancel = async () => {
    if (!cancelConfirm) return;
    setCancelSaving(true);
    setErr(null);
    try {
      await contactsAPI.cancelScheduledCallback(contactId, cancelConfirm);
      setCancelConfirm(null);
      await fetchList();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Cancel failed');
    } finally {
      setCancelSaving(false);
    }
  };

  return (
    <section
      className="bg-surface-1 border border-white/[0.08] rounded-ds-xl p-4 mt-4"
      aria-labelledby="scheduled-callbacks-heading"
    >
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-accent-text" />
          <h3
            id="scheduled-callbacks-heading"
            className="text-sm font-semibold text-slate-50"
          >
            Scheduled callbacks
          </h3>
          {pending.length > 0 ? (
            <Pill intent="warn">{pending.length} pending</Pill>
          ) : null}
        </div>
        <Button
          intent="secondary"
          size="sm"
          onClick={() => {
            setShowCreate(true);
            setCreateWhen(isoLocalForInput(new Date(Date.now() + 60 * 60 * 1000)));
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Schedule</span>
        </Button>
      </header>

      {err ? (
        <div className="mb-3 text-xs text-danger-text border border-danger-border rounded-ds-lg px-3 py-2 bg-danger-tint">
          {err}
        </div>
      ) : null}

      {loading ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-slate-400">
          No callbacks scheduled. Use “Schedule” to create one.
        </div>
      ) : (
        <ul className="divide-y divide-white/[0.06]">
          {[...pending, ...history].map((c) => (
            <li key={c.id} className="py-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-50 font-mono">{fmt(c.scheduled_at)}</span>
                  <Pill intent={STATUS_INTENT[c.status]}>{STATUS_LABEL[c.status]}</Pill>
                </div>
                {c.notes ? (
                  <div className="text-xs text-slate-400 mt-0.5 truncate">{c.notes}</div>
                ) : null}
                {c.status === 'cancelled' && c.cancelled_reason ? (
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Cancelled: {c.cancelled_reason}
                  </div>
                ) : null}
                {c.status === 'failed_to_fire' && c.cancelled_reason ? (
                  <div className="text-[11px] text-danger-text mt-0.5">
                    Failed: {c.cancelled_reason}
                  </div>
                ) : null}
              </div>
              {c.status === 'pending' ? (
                <div className="flex items-center gap-1 shrink-0">
                  <IconButton
                    intent="ghost"
                    size="sm"
                    aria-label="Reschedule"
                    title="Reschedule"
                    onClick={() => {
                      setRescheduling(c.id);
                      setRescheduleWhen(isoLocalForInput(c.scheduled_at));
                    }}
                  >
                    <RotateCcw size={14} />
                  </IconButton>
                  <IconButton
                    intent="ghost"
                    size="sm"
                    aria-label="Cancel"
                    title="Cancel"
                    onClick={() => setCancelConfirm(c.id)}
                  >
                    <X size={14} />
                  </IconButton>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {/* Create modal */}
      {showCreate ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !creating && setShowCreate(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="schedule-callback-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="schedule-callback-title"
              className="text-lg font-semibold text-slate-50 mb-3"
            >
              Schedule callback
            </h2>
            <Input
              label="When"
              type="datetime-local"
              value={createWhen}
              onChange={(e) => setCreateWhen(e.target.value)}
              disabled={creating}
            />
            {/* LT-390: agent / sequence / campaign pickers. Backend already
                stores these snapshot IDs on the commitment (LT-321 S3); the
                UI just wasn't surfacing them, so callbacks would fall back
                to the workspace default agent at fire time. */}
            <Select
              label="Agent"
              value={createAgentId}
              onChange={(e) => setCreateAgentId(e.target.value)}
              disabled={creating || optionsLoading}
              hint={
                optionsLoading
                  ? 'Loading agents…'
                  : 'Defaults to the workspace agent if left empty.'
              }
              className="mt-3"
            >
              <option value="">— Workspace default —</option>
              {(agentOptions ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                  {a.is_system ? ' (system)' : ''}
                </option>
              ))}
            </Select>
            <Select
              label="Sequence (optional)"
              value={createSequenceId}
              onChange={(e) => setCreateSequenceId(e.target.value)}
              disabled={creating || optionsLoading}
              className="mt-3"
            >
              <option value="">— None —</option>
              {(sequenceOptions ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
            <Select
              label="Campaign (optional)"
              value={createCampaignId}
              onChange={(e) => setCreateCampaignId(e.target.value)}
              disabled={creating || optionsLoading}
              className="mt-3"
            >
              <option value="">— None —</option>
              {(campaignOptions ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Input
              label="Notes (optional)"
              value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              placeholder="What did the lead ask for?"
              disabled={creating}
              className="mt-3"
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !creating && setShowCreate(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button intent="primary" onClick={handleCreate} disabled={creating}>
                {creating ? 'Saving…' : 'Schedule'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Reschedule modal */}
      {rescheduling ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !rescheduleSaving && setRescheduling(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="reschedule-callback-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="reschedule-callback-title"
              className="text-lg font-semibold text-slate-50 mb-3"
            >
              Reschedule callback
            </h2>
            <Input
              label="New time"
              type="datetime-local"
              value={rescheduleWhen}
              onChange={(e) => setRescheduleWhen(e.target.value)}
              disabled={rescheduleSaving}
            />
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !rescheduleSaving && setRescheduling(null)}
                disabled={rescheduleSaving}
              >
                Back
              </Button>
              <Button
                intent="primary"
                onClick={handleReschedule}
                disabled={rescheduleSaving}
              >
                {rescheduleSaving ? 'Saving…' : 'Reschedule'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Cancel confirm */}
      {cancelConfirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !cancelSaving && setCancelConfirm(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cancel-callback-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="cancel-callback-title"
              className="text-lg font-semibold text-slate-50 mb-2"
            >
              Cancel scheduled callback?
            </h2>
            <p className="text-sm text-slate-300">
              The commitment will be marked as cancelled and won’t fire. The
              contact’s lifecycle stage isn’t changed automatically — set it
              from the Lifecycle field if needed.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !cancelSaving && setCancelConfirm(null)}
                disabled={cancelSaving}
              >
                Back
              </Button>
              <Button intent="danger" onClick={handleCancel} disabled={cancelSaving}>
                {cancelSaving ? 'Cancelling…' : 'Cancel callback'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default ScheduledCallbacksSection;
